import buildParams from 'src/internal/analytics/buildParams';
import {getDefaultRpcInstance} from 'src/internal/rpc/defaultRpc';

import type {
  IAnalytics,
  ImperativeAnalytics,
} from 'src/internal/analytics/types';

type AnalyticsMethodName = keyof IAnalytics;

const rpcMethodMap = {
  action: 'analyticsAction',
  link: 'analyticsLink',
  modal: 'analyticsModal',
  track: 'analyticsTrack',
  viewed: 'analyticsViewed',
} as const;

const method = <TMethod extends AnalyticsMethodName>(
  analyticsMethodName: TMethod,
): ImperativeAnalytics[TMethod] => {
  return (config, eventName, parameters): void => {
    if (!config || config.analytics?.disabled) {
      return;
    }

    const injectedMethod =
      config._internal_observability?.analytics?.[analyticsMethodName];

    if (injectedMethod) {
      injectedMethod(config, eventName, buildParams(config, parameters));
      return;
    }

    const rpcClient = config.rpc ?? getDefaultRpcInstance().client;
    const rpcMethod = rpcMethodMap[analyticsMethodName];

    rpcClient.call(
      rpcMethod,
      // @ts-expect-error - TS2345 - Argument of type '[Readonly<{ service?: string | undefined; project: ProjectName; page?: string | undefined; pageViewToken?: string | undefined; release?: string | undefined; userId?: string | undefined; ... 4 more ...; _internal_observability?: InternalObservabilityConfig | undefined; }>, string, AnalyticsParams<...> | undefined]' is not assignable to parameter of type 'ObservabilityRpcMethods[{ readonly action: "analyticsAction"; readonly link: "analyticsLink"; readonly modal: "analyticsModal"; readonly track: "analyticsTrack"; readonly viewed: "analyticsViewed"; }[TMethod]]["args"]'.
      config,
      eventName,
      parameters,
    );
  };
};

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#action
 */
export const action = method('action');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#link
 */
export const link = method('link');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#modal
 */
export const modal = method('modal');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#track
 */
export const track = method('track');

/**
 * @see https://sail.stripe.me/observability/api/useAnalytics#viewed
 */
export const viewed = method('viewed');
